import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import IcnChevronRight from "@assets/icons/chevron-right-sharp.svg";
import IcnCopy from "@assets/icons/copy.svg";
import { formatMessage } from "@app/translations/intl";
import { getOptimizedImageUrl } from "@app/util/image-optimization";
import { RelAttributes } from "@app/constants";
import { CampaignStatusComponent } from "@app/core/campaign-status-label/campaign-status-label.component";
import { ClickableComponent } from "@app/core/clickable";
import { FeaturedFilterLabel } from "@app/core/featured-filter-label/featured-filter-label";
import { ImageComponent } from "@app/core/image";
import { Icon } from "@app/components/icon/icon";
import { IOutFeaturedMerchantDTO } from "@app/core/new-merchant-card/featured-merchants.interface";
import { getActiveTileCoupon } from "@app/util/store-tile-coupon";

import styles from "./merchant-card-v3.module.scss";

export interface IMerchantCardV3Props {
  merchant: IOutFeaturedMerchantDTO;
  onClick?: () => void;
  platformId: number;
  responsive?: boolean;
  variant?: "normal" | "mobile-search";
}

const component = (props: IMerchantCardV3Props) => {
  const {
    merchant,
    responsive,
    merchant: { campaign, slug }
  } = props;

  const featuredFilter = campaign?.featuredFilter;
  const outMerchantLink = getMerchantCardLink(slug);
  // Re-checked after mount so a cached server render can't keep showing an expired code.
  const [couponCode, setCouponCode] = useState(() => getActiveTileCoupon(campaign));
  const [couponCopied, setCouponCopied] = useState(false);

  useEffect(() => {
    setCouponCode(getActiveTileCoupon(campaign));
  }, [campaign]);

  const handleCouponClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!couponCode) return;

    // The navbar search closes on any tile click; keep it open so the "copied" label stays visible.
    event.stopPropagation();

    copyToClipboard(couponCode);
    setCouponCopied(true);
    window.open(outMerchantLink, "_blank", "noopener");

    if (window && window.dataLayer) {
      window.dataLayer.push({
        event: "storeTileCouponClick",
        merchantId: merchant.id,
        merchantSlug: slug,
        couponCode
      });
    }
  };
  const internalMerchantLink = `${formatMessage({ id: "global.merchantOverviewLink" })}${slug}/`;
  const cardBorderStyles = clsx(
    (props.variant === "normal" || !props.variant) && styles.normal,
    props.variant === "mobile-search" && styles.mobileSearch
  );

  return (
    <div
      className={clsx(
        styles.merchantCard,
        responsive && styles.responsive,
        cardBorderStyles,
        couponCode && styles.withCoupon
      )}
    >
      <a className={styles.header} href={outMerchantLink} rel={RelAttributes.SPONSORED} target="_blank">
        <div className={styles.campaignStatus}>
          <CampaignStatusComponent durationStatus={campaign?.durationStatus} />
        </div>
        {featuredFilter?.name && (
          <div className={styles.featuredLabel}>
            <FeaturedFilterLabel
              className={styles.label}
              label={featuredFilter?.label}
              text={featuredFilter?.name}
              corners="diagonal-right"
            />
          </div>
        )}
        <div className={styles.image}>
          <ImageComponent
            alt={merchant.logoImage?.alt || ""}
            src={getOptimizedImageUrl({ url: merchant.logoImage?.url, height: 120, quality: 100 })}
            title={merchant.logoImage?.title || ""}
            forceImageTag
            isBlocking
          />
        </div>
      </a>
      <div className={styles.content}>
        {merchant.name && (
          <Link onClick={props.onClick} className={styles.merchant} to={internalMerchantLink}>
            {merchant.name}
          </Link>
        )}
        <a
          className={clsx(styles.description, !campaign?.excerpt && styles.emptyDescription)}
          href={outMerchantLink}
          rel="sponsored"
          target="_blank"
        >
          {campaign?.excerpt}
        </a>
        <div className={styles.button}>
          <ClickableComponent
            variant="primary-2025"
            iconStyle="filled"
            title={formatMessage({ id: "merchantCard.button.toCampaignPageOut" })}
            iconRight={IcnChevronRight}
            href={outMerchantLink}
            fullWidth
            forceExternal
            target="_blank"
            relAttribute={RelAttributes.SPONSORED}
          />
        </div>
        {couponCode ? (
          <button
            type="button"
            className={styles.couponLink}
            onClick={handleCouponClick}
            data-hint={formatMessage({ id: "merchantCard.couponHint" })}
            aria-label={`${formatMessage({ id: "merchantCard.couponLabel" })} ${couponCode}`}
          >
            {couponCopied ? (
              <span className={styles.couponValue}>
                <span className={styles.couponCopied}>{formatMessage({ id: "merchantCard.couponCopied" })}</span>
                <Icon icon={IcnCopy} fillColor="#8204ff" size="12px" />
              </span>
            ) : (
              <>
                <span className={styles.couponLabel}>{formatMessage({ id: "merchantCard.couponLabel" })}</span>
                <span className={styles.couponValue}>
                  <span className={styles.couponCode}>{couponCode}</span>
                  <Icon icon={IcnCopy} fillColor="#8204ff" size="12px" />
                </span>
              </>
            )}
          </button>
        ) : (
          <a className={styles.outMerchantLink} target="_blank" rel="sponsored" href={outMerchantLink}>
            <span className={styles.merchantLinkText}>{formatMessage({ id: "dealCard.showAllMerchantDeals" })}</span>
            <Icon icon={IcnChevronRight} fillColor="#999999" size="9px" />
          </a>
        )}
      </div>
    </div>
  );
};

const MerchantCardV3 = React.memo(component);
export { MerchantCardV3 };

// Synchronous copy first: the campaign tab opened right after takes focus, which makes the async Clipboard API fail.
const copyToClipboard = (value: string) => {
  const tempInput = document.createElement("input");
  tempInput.style.position = "absolute";
  tempInput.style.left = "-1000px";
  tempInput.style.top = "-1000px";
  tempInput.value = value;
  document.body.appendChild(tempInput);
  tempInput.select();
  /* tslint:disable-next-line deprecation */
  const copied = document.execCommand("copy");
  document.body.removeChild(tempInput);

  if (!copied && navigator.clipboard) {
    navigator.clipboard.writeText(value).catch(() => undefined);
  }
};

const getMerchantCardLink = (slug?: string) => {
  if (!slug) return "";

  const urlAddition = formatMessage({ id: "global.urlAddition" });

  return urlAddition ? `/out/store/${urlAddition}/${slug}/` : `/out/store/${slug}/`;
};
